<script setup lang="ts">
import AnimatedLogo from '../components/AnimatedLogo.vue'

const prefetchAdmin = () => {
  // Warm the cache for the post-login route and initial dashboard
  import('../views/AdminView.vue')
  import('../views/DashboardView.vue')
}

// Opportunistically prefetch when the browser is idle
if ('requestIdleCallback' in window) {
  ;(window as any).requestIdleCallback(prefetchAdmin)
} else {
  setTimeout(prefetchAdmin, 1500)
}

const workflowSteps = [
  {
    number: 1,
    title: 'Define',
    description: 'Set up your sound categories'
  },
  {
    number: 2,
    title: 'Provide',
    description: 'Provide sample audio files'
  },
  {
    number: 3,
    title: 'Classify',
    description: 'Categorize your events'
  },
  {
    number: 4,
    title: 'Train',
    description: 'Create your model'
  }
]
</script>

<template>
  <main class="w-full bg-white dark:bg-brand-dark">
    <!-- Hero Section -->
    <section class="relative overflow-hidden py-40 hero-gradient" aria-labelledby="hero-heading">
      <div class="page-container mx-auto">
        <div class="flex flex-col md:flex-row items-center justify-between gap-20 mb-20">
          <div class="text-center md:text-left space-y-8 md:flex-1">
            <h1
              id="hero-heading"
              class="text-7xl font-extrabold tracking-tight leading-tight whitespace-nowrap"
            >
              <span class="letter-reveal">N</span>
              <span class="letter-reveal">o</span>
              <span class="letter-reveal">i</span>
              <span class="letter-reveal">s</span>
              <span class="letter-reveal">e</span>
              <span class="letter-reveal">T</span>
              <span class="letter-reveal">a</span>
              <span class="letter-reveal">g</span>
            </h1>

            <p class="text-2xl sm:text-3xl text-gray-900 dark:text-gray-300 leading-relaxed">
              AI-powered sound event classification integrated into
              <span class="brand-highlight">NorCloud</span>.
            </p>
          </div>

          <div class="md:flex-1 hidden md:flex justify-center md:justify-end items-center">
            <AnimatedLogo class="w-96 h-96 text-brand-orange" />
          </div>
        </div>
      </div>
    </section>

    <!-- Problem Section -->
    <section class="py-40 bg-gray-50 dark:bg-gray-800">
      <div class="page-container mx-auto">
        <div class="text-center mb-20">
          <h2 class="text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Why Separating Activity and Non-Activity Sounds is Critical
          </h2>
          <p class="text-2xl text-gray-600 dark:text-gray-300">
            Recorded audio rarely contains only the sound of the activity under study.
          </p>
        </div>

        <div
          class="bg-white dark:bg-gray-700 p-20 rounded-xl border border-gray-200 dark:border-gray-600 text-center shadow-sm"
        >
          <p class="text-3xl font-semibold text-gray-900 dark:text-white mb-10 leading-relaxed">
            Traffic, nearby industry, weather-related noise and other sources are often captured
            together with the target sounds.
          </p>
          <p class="text-2xl text-gray-600 dark:text-gray-300">
            Identifying which events belong to the activity-and which ones do not-is one of the most
            time-consuming and subjective parts of analysis, especially in long-term or unattended
            monitoring projects.
          </p>
        </div>
      </div>
    </section>

    <!-- Solution Section -->
    <section class="py-40 bg-white dark:bg-gray-900">
      <div class="page-container mx-auto">
        <div class="grid md:grid-cols-2 gap-20 items-start">
          <div class="space-y-8 text-center md:text-left">
            <h2 class="text-6xl font-bold text-gray-900 dark:text-white">
              <span class="text-brand-orange">NoiseTag</span> Automates Classification
            </h2>
            <p class="text-2xl text-gray-600 dark:text-gray-300 leading-relaxed">
              NoiseTag automatically classifies noise sources during monitoring, identifying which
              events are relevant to the activity being studied and which are not. Irrelevant noise
              can be excluded from analysis.
            </p>
          </div>

          <div class="grid sm:grid-cols-3 gap-8 justify-items-center">
            <div class="card-feature">
              <div class="text-5xl mb-6">⏱️</div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                Shorter Analysis Time
              </h3>
              <p class="text-lg text-gray-600 dark:text-gray-400">Less manual review</p>
            </div>
            <div class="card-feature">
              <div class="text-5xl mb-6">✅</div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                Improved Consistency
              </h3>
              <p class="text-lg text-gray-600 dark:text-gray-400">More uniform results</p>
            </div>
            <div class="card-feature">
              <div class="text-5xl mb-6">🎯</div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white">Better Accuracy</h3>
              <p class="text-lg text-gray-600 dark:text-gray-400">Focus on the right events</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-40 bg-gray-50 dark:bg-gray-800">
      <div class="page-container mx-auto">
        <div class="text-center mb-24">
          <h2 class="text-6xl font-bold text-gray-900 dark:text-white mb-4">
            How <span class="brand-highlight">NoiseTag</span> Works
          </h2>
          <p class="text-2xl text-gray-600 dark:text-gray-400">
            Integrated seamlessly into NorCloud
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-12">
          <div
            class="bg-white dark:bg-gray-900 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="flex items-center gap-6 mb-4">
              <div class="text-7xl flex-shrink-0">🔌</div>
              <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">
                Seamless Integration
              </h3>
            </div>
            <p class="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              Works directly on incoming sound events within
              <span class="brand-highlight">NorCloud</span>. No additional hardware required, setup
              takes only a few minutes.
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-900 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="flex items-center gap-6 mb-4">
              <div class="text-7xl flex-shrink-0">🎓</div>
              <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">Project-Based</h3>
            </div>
            <p class="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              Train using representative audio samples from your site. Only a small number of
              samples is sufficient for reliable classification.
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-900 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="flex items-center gap-6 mb-4">
              <div class="text-7xl flex-shrink-0">🏞️</div>
              <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">Site-Specific</h3>
            </div>
            <p class="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              Adapts quickly to specific acoustic conditions of each site. Classification based on
              sounds that actually occur within the project.
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-900 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="flex items-center gap-6 mb-4">
              <div class="text-7xl flex-shrink-0">📊</div>
              <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">
                Confidence Scoring
              </h3>
            </div>
            <p class="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              Each event presented with confidence score and percentage value showing how certain
              the model is.
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-900 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="flex items-center gap-6 mb-4">
              <div class="text-7xl flex-shrink-0">📋</div>
              <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">User-Defined</h3>
            </div>
            <p class="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              All defined source labels are considered part of the activity by default, except those
              explicitly marked as non-activity.
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-900 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="flex items-center gap-6 mb-4">
              <div class="text-7xl flex-shrink-0">🔄</div>
              <h3 class="text-2xl font-semibold text-gray-900 dark:text-white">Model Retraining</h3>
            </div>
            <p class="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              Can be retrained at any point to reflect changes in source labels or classification
              criteria during long-term projects.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Workflow Section -->
    <section class="py-40 bg-white dark:bg-gray-900">
      <div class="page-container mx-auto">
        <div class="text-center mb-24">
          <h2 class="text-6xl font-bold text-gray-900 dark:text-white mb-4">Getting Started</h2>
          <p class="text-2xl text-gray-600 dark:text-gray-400">
            Four simple steps to automated classification
          </p>
        </div>

        <div class="grid md:grid-cols-4 gap-10">
          <div v-for="step in workflowSteps" :key="step.number" class="relative">
            <!-- Connector line -->
            <div
              v-if="step.number < workflowSteps.length"
              class="hidden md:block absolute top-20 left-full w-full h-1 bg-gradient-to-r from-gray-300 to-transparent dark:from-gray-600"
            />

            <!-- Step card -->
            <div
              class="bg-white dark:bg-gray-800 p-10 rounded-lg border border-gray-200 dark:border-gray-700 text-center relative z-10"
            >
              <div
                class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-orange text-white font-bold text-3xl mb-6"
              >
                {{ step.number }}
              </div>
              <h3 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                {{ step.title }}
              </h3>
              <p class="text-lg text-gray-600 dark:text-gray-400">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Capabilities Section -->
    <section class="py-40 bg-gray-50 dark:bg-gray-800">
      <div class="page-container mx-auto">
        <div class="text-center mb-24">
          <h2 class="text-6xl font-bold text-gray-900 dark:text-white mb-4">
            Calculate activity-only noise levels
          </h2>
        </div>

        <div
          class="bg-white dark:bg-gray-700 p-20 rounded-xl border border-gray-200 dark:border-gray-600 text-center shadow-sm mx-auto"
        >
          <p class="text-2xl text-gray-900 dark:text-white mb-8 leading-relaxed">
            <span class="brand-highlight">NoiseTag</span> enables
            <span class="brand-highlight">NorCloud</span> to calculate partial noise levels by
            excluding non-activity contributions.
          </p>
          <p class="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
            Depending on the project requirements, these contributions may be fully excluded,
            replaced by a representative background level, or addressed using percentile-based
            approaches, allowing the most appropriate method to be applied in each case.
          </p>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="py-40 bg-white dark:bg-gray-900">
      <div class="page-container mx-auto">
        <div class="text-center mb-24">
          <h2 class="text-6xl font-bold text-gray-900 dark:text-white mb-4">Typical Use Cases</h2>
          <p class="text-2xl text-gray-600 dark:text-gray-400">
            Particularly useful where multiple sound sources are present and only part of the
            measured noise is relevant
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-10">
          <div
            class="flex gap-6 items-start bg-gray-50 dark:bg-gray-800 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="text-brand-orange text-4xl font-bold flex-shrink-0">✓</div>
            <p class="text-xl text-gray-900 dark:text-white leading-relaxed">
              Acoustic projects with multiple overlapping sound sources
            </p>
          </div>
          <div
            class="flex gap-6 items-start bg-gray-50 dark:bg-gray-800 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="text-brand-orange text-4xl font-bold flex-shrink-0">✓</div>
            <p class="text-xl text-gray-900 dark:text-white leading-relaxed">
              Environmental noise monitoring influenced by non-activity noise
            </p>
          </div>
          <div
            class="flex gap-6 items-start bg-gray-50 dark:bg-gray-800 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="text-brand-orange text-4xl font-bold flex-shrink-0">✓</div>
            <p class="text-xl text-gray-900 dark:text-white leading-relaxed">
              Compliance studies requiring activity-only noise levels
            </p>
          </div>
          <div
            class="flex gap-6 items-start bg-gray-50 dark:bg-gray-800 p-10 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div class="text-brand-orange text-4xl font-bold flex-shrink-0">✓</div>
            <p class="text-xl text-gray-900 dark:text-white leading-relaxed">
              Long-term unattended monitoring with a high volume of noise events
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-48 bg-gray-900 dark:bg-black">
      <div class="page-container mx-auto text-center">
        <h2 class="text-6xl font-bold text-white mb-10 leading-tight">
          Start Classifying Sounds Today
        </h2>
        <p class="text-2xl text-gray-300 mb-16 leading-relaxed">
          Get up and running in minutes. No technical expertise required-just your audio and
          <span class="brand-highlight">NoiseTag</span> inside
          <span class="brand-highlight">NorCloud</span>.
        </p>

        <router-link
          :to="{ name: 'login' }"
          @mouseenter="prefetchAdmin"
          class="inline-flex items-center justify-center px-12 py-5 rounded-lg bg-brand-orange text-white font-semibold text-lg hover:bg-brand-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange transition-colors"
        >
          Get Started Now
        </router-link>
      </div>
    </section>

    <!-- Footer -->
    <footer class="py-8 bg-gray-950 dark:bg-gray-950 border-t border-gray-800">
      <div class="page-container max-w-6xl mx-auto text-center text-sm text-gray-400">
        <p>
          <span class="brand-highlight">NoiseTag</span> - AI-powered environmental noise
          classification by Norsonic, built for <span class="brand-highlight">NorCloud</span>
        </p>
      </div>
    </footer>
  </main>
</template>

<style scoped>
.page-container {
  @apply max-w-6xl mx-auto px-4 sm:px-6 lg:px-8;
}

.card-feature {
  @apply w-full text-center bg-white dark:bg-gray-900 p-8 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm;
}

.brand-highlight {
  @apply font-semibold text-brand-orange;
}

/* Hero Section Styles */
.hero-gradient {
  @apply bg-gradient-to-br from-brand-neutral-50 to-brand-neutral-200 dark:bg-gradient-to-br dark:from-brand-dark  dark:to-gray-950;
}

.letter-reveal {
  background: linear-gradient(to right, #003c71, #01273e);
  display: inline-block;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.dark .letter-reveal {
  background: linear-gradient(to right, #bacad8, #f0f4f7);
  display: inline-block;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
</style>
